import { useState, useMemo } from "react";
import { toast } from "react-hot-toast";
import { Users, UserPlus, X, Send, ShieldCheck, UserCheck, Info } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select } from "@/components/ui/select";
import { useSettings } from "../hooks/useSettings";
import {
  inviteRoleOptions,
  type TeamMember,
  type TeamRole,
  type PendingInvite,
} from "../mockSettings";

export function TeamSection() {
  const { userProfile, isLoading } = useSettings();

  // The workspace owner is the authenticated user
  const currentUser: TeamMember = useMemo(() => ({
    id: "owner-self",
    name: userProfile.fullName || userProfile.email || "Workspace Owner",
    email: userProfile.email || "owner@yourbusiness.com",
    role: "Owner" as TeamRole,
  }), [userProfile]);

  const [invitedMembers, setInvitedMembers] = useState<TeamMember[]>([]);
  const [pendingInvites, setPendingInvites] = useState<PendingInvite[]>([]);
  const [confirmRemoveId, setConfirmRemoveId] = useState<string | null>(null);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState<TeamRole>("Agent");

  const allMembers = [currentUser, ...invitedMembers];

  const handleRoleChange = (id: string, role: TeamRole) => {
    setInvitedMembers((prev) => prev.map((m) => (m.id === id ? { ...m, role } : m)));
    toast.success("Role updated");
  };

  const handleRemove = (id: string) => {
    if (confirmRemoveId !== id) {
      setConfirmRemoveId(id);
      return;
    }
    setInvitedMembers((prev) => prev.filter((m) => m.id !== id));
    setConfirmRemoveId(null);
    toast.success("Member removed");
  };

  const handleSendInvite = () => {
    if (!inviteEmail.trim() || !inviteEmail.includes("@")) {
      toast.error("Enter a valid email address");
      return;
    }
    const email = inviteEmail.trim().toLowerCase();
    if (email === currentUser.email.toLowerCase() || allMembers.some((m) => m.email.toLowerCase() === email)) {
      toast.error("This user is already part of the workspace");
      return;
    }
    if (pendingInvites.some((i) => i.email.toLowerCase() === email)) {
      toast.error("An invite has already been sent to this email");
      return;
    }

    setPendingInvites((prev) => [
      ...prev,
      { id: `inv-${Date.now()}`, email, role: inviteRole },
    ]);
    setInviteEmail("");
    toast.success(`Invite sent to ${email}`);
  };

  const handleCancelInvite = (id: string) => {
    setPendingInvites((prev) => prev.filter((i) => i.id !== id));
    toast.success("Invite canceled");
  };

  return (
    <div className="space-y-5">
      {/* Team Roster Card */}
      <Card className="max-w-none overflow-hidden rounded-2xl border border-slate-200/90 dark:border-slate-800 bg-slate-50/40 dark:bg-slate-900/40 shadow-xs">
        <div className="flex items-center justify-between border-b border-slate-200/70 dark:border-slate-800/80 bg-slate-100/50 dark:bg-slate-850/60 px-3.5 py-2">
          <div className="flex items-center gap-2">
            <span className="flex h-5 w-5 items-center justify-center rounded-lg bg-primary/10 text-primary">
              <Users className="h-3 w-3" />
            </span>
            <span className="text-[11px] font-bold tracking-wider uppercase text-slate-800 dark:text-slate-200">
              Team Members &amp; Roles
            </span>
            <span className="inline-flex items-center rounded-full border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-1.5 py-0.2 text-[9px] font-bold text-slate-500 dark:text-slate-400">
              {allMembers.length} member{allMembers.length > 1 ? "s" : ""}
            </span>
          </div>
          <span className="text-[10px] font-medium text-slate-400 dark:text-slate-500">Workspace access permissions</span>
        </div>

        <div className="p-3.5 sm:p-4 bg-white/70 dark:bg-slate-900/70 space-y-2.5">
          {/* Members list */}
          {allMembers.map((member) => (
            <div
              key={member.id}
              className="flex flex-col gap-2.5 rounded-xl border border-slate-200/80 dark:border-slate-800 bg-white dark:bg-slate-850 p-2.5 shadow-2xs sm:flex-row sm:items-center sm:justify-between"
            >
              <div className="min-w-0 flex items-center gap-2.5">
                <div className="flex h-8 w-8 items-center justify-center rounded-xl bg-primary/10 text-primary font-bold text-xs">
                  {member.name.slice(0, 2).toUpperCase()}
                </div>
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="truncate text-xs font-bold text-slate-800 dark:text-slate-200">{member.name}</span>
                    <span className="inline-flex items-center rounded-md border border-emerald-200/80 dark:border-emerald-800/60 bg-emerald-50 dark:bg-emerald-950/40 px-1.5 py-0.2 text-[9px] font-bold text-emerald-700 dark:text-emerald-300">
                      {member.role}
                    </span>
                    {member.role === "Owner" && (
                      <span className="text-[10px] text-slate-400 dark:text-slate-500 font-medium">(You)</span>
                    )}
                  </div>
                  <p className="truncate text-[11px] text-slate-400 dark:text-slate-500 font-medium">{member.email}</p>
                </div>
              </div>

              {member.role !== "Owner" && (
                <div className="flex items-center gap-1.5">
                  <Select
                    aria-label={`Change role for ${member.name}`}
                    value={member.role}
                    onChange={(e) => handleRoleChange(member.id, e.target.value as TeamRole)}
                    className="h-8 text-xs w-24 rounded-xl border-slate-200 dark:border-slate-800"
                  >
                    <option value="Admin">Admin</option>
                    <option value="Agent">Agent</option>
                  </Select>
                  <Button
                    type="button"
                    variant={confirmRemoveId === member.id ? "destructive" : "outline"}
                    size="sm"
                    className="h-8 px-3 text-xs font-semibold rounded-xl shadow-2xs border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800"
                    onClick={() => handleRemove(member.id)}
                    onBlur={() => setConfirmRemoveId(null)}
                  >
                    {confirmRemoveId === member.id ? "Confirm?" : "Remove"}
                  </Button>
                </div>
              )}
            </div>
          ))}

          {/* Guide when only the owner is present */}
          {invitedMembers.length === 0 && (
            <div className="flex items-start gap-2.5 rounded-xl border border-dashed border-slate-200 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-850/40 p-3 text-xs text-slate-500 dark:text-slate-400">
              <Info className="h-4 w-4 text-primary shrink-0 mt-0.5" />
              <div className="space-y-1">
                <p className="font-semibold text-slate-700 dark:text-slate-300">No additional team members yet</p>
                <p className="text-[11px] leading-relaxed">
                  Invite your colleagues using the form below to delegate customer support conversations and order management.
                  <br />
                  &bull; <strong className="text-slate-700 dark:text-slate-300">Admin:</strong> Full access to channel integrations, automation rules, and workspace settings.
                  <br />
                  &bull; <strong className="text-slate-700 dark:text-slate-300">Agent:</strong> Access to the omnichannel inbox, leads, and customer messaging.
                </p>
              </div>
            </div>
          )}
        </div>
      </Card>

      {/* Invite Member Card */}
      <Card className="max-w-none overflow-hidden rounded-2xl border border-slate-200/90 dark:border-slate-800 bg-slate-50/40 dark:bg-slate-900/40 shadow-xs">
        <div className="flex items-center justify-between border-b border-slate-200/70 dark:border-slate-800/80 bg-slate-100/50 dark:bg-slate-850/60 px-3.5 py-2">
          <div className="flex items-center gap-2">
            <span className="flex h-5 w-5 items-center justify-center rounded-lg bg-emerald-500/10 text-emerald-600 dark:text-emerald-400">
              <UserPlus className="h-3 w-3" />
            </span>
            <span className="text-[11px] font-bold tracking-wider uppercase text-slate-800 dark:text-slate-200">
              Invite a Team Member
            </span>
          </div>
          <span className="text-[10px] font-medium text-slate-400 dark:text-slate-500">Send email invite</span>
        </div>

        <div className="p-3.5 sm:p-4 bg-white/70 dark:bg-slate-900/70 space-y-3">
          <div className="flex flex-col gap-2 sm:flex-row">
            <div className="flex-1">
              <Label htmlFor="invite-email" className="text-[10px] font-semibold text-slate-600 dark:text-slate-400">Email address</Label>
              <Input
                id="invite-email"
                type="email"
                placeholder="colleague@yourbusiness.com"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                className="mt-1 h-9 rounded-xl"
              />
            </div>
            <div className="sm:w-36">
              <Label htmlFor="invite-role" className="text-[10px] font-semibold text-slate-600 dark:text-slate-400">Role</Label>
              <Select
                id="invite-role"
                value={inviteRole}
                onChange={(e) => setInviteRole(e.target.value as TeamRole)}
                className="mt-1 h-9 rounded-xl"
              >
                {inviteRoleOptions.map((role) => (
                  <option key={role} value={role}>
                    {role}
                  </option>
                ))}
              </Select>
            </div>
            <div className="flex items-end">
              <Button type="button" onClick={handleSendInvite} className="h-9 px-5 text-sm font-semibold rounded-xl shadow-xs w-full sm:w-auto gap-1.5">
                <Send className="h-3.5 w-3.5" />
                Send invite
              </Button>
            </div>
          </div>

          {/* Pending Invites list / Empty guide */}
          <div className="border-t border-slate-100 dark:border-slate-800 pt-3">
            <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 dark:text-slate-500">
              Pending Invites
            </span>
            {pendingInvites.length > 0 ? (
              <div className="mt-2 space-y-1.5">
                {pendingInvites.map((invite) => (
                  <div
                    key={invite.id}
                    className="flex items-center justify-between rounded-xl border border-slate-200/80 dark:border-slate-800 bg-white dark:bg-slate-850 px-3 py-2 shadow-2xs"
                  >
                    <div className="min-w-0">
                      <p className="truncate text-xs font-semibold text-slate-800 dark:text-slate-200">{invite.email}</p>
                      <p className="text-[10px] text-slate-400 dark:text-slate-500 font-medium">Invited as {invite.role}</p>
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      aria-label={`Cancel invite to ${invite.email}`}
                      className="h-6 w-6 text-slate-400 hover:text-rose-600"
                      onClick={() => handleCancelInvite(invite.id)}
                    >
                      <X className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                ))}
              </div>
            ) : (
              <p className="mt-1 text-xs text-slate-400 dark:text-slate-500">No pending invites. Sent invitations will show here until accepted.</p>
            )}
          </div>
        </div>
      </Card>
    </div>
  );
}

export default TeamSection;
